import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs/operators';
import {
  YdsButtonComponent,
  YdsButtonBackComponent,
  YdsCardComponent,
  YdsDropdownComponent,
  YdsSpinnerComponent,
  YdsSwitchComponent,
  type YdsDropdownOption,
} from '../../../../yds-angular';
import { ConfirmDialogService } from '../../../../dialog/confirm-dialog/confirm-dialog.service';
import { isInvalid } from '../../../../shared/general-ledger-yds.utils';
import {
  BookingGroup,
  CreatebireportService,
  CreateJournalPlanningRecordForBIRequest,
} from '../createbireport.service';

@Component({
  selector: 'app-createbireport-editable',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    YdsButtonComponent,
    YdsButtonBackComponent,
    YdsCardComponent,
    YdsDropdownComponent,
    YdsSpinnerComponent,
    YdsSwitchComponent,
  ],
  templateUrl: './createbireport-editable.component.html',
  styleUrl: './createbireport-editable.component.scss',
})
export class CreatebireportEditableComponent {
  private formBuilder = inject(FormBuilder);
  private createbireportService = inject(CreatebireportService);
  private dialogService = inject(ConfirmDialogService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  /** Guid of the import run this journal planning record is created for. */
  readonly importRunId = signal<string>('');
  /** Name of the payroll client (opdrachtgever), passed through router navigation state. */
  readonly opdrachtgeverName = signal<string>('');
  /** Selection on the previous page, restored when navigating back. */
  private opdrachtgeverId: string | null = null;
  private instellingId: string | null = null;
  readonly bookingGroups = signal<BookingGroup[]>([]);
  readonly bookingGroupOptions = computed<YdsDropdownOption[]>(() =>
    this.bookingGroups().map((group) => ({
      id: group.booking_Group_ID,
      label: group.bookingGroup_Name,
    })),
  );

  readonly isSaving = signal(false);
  readonly isInvalid = isInvalid;

  readonly form: FormGroup = this.formBuilder.group({
    bookingGroupId: ['', Validators.required],
    applyWagePartitioning: [true],
    applyRedistribution: [true],
  });

  constructor() {
    const importRunId = this.route.snapshot.paramMap.get('importRunId') ?? '';
    this.importRunId.set(importRunId);

    const state = this.router.getCurrentNavigation()?.extras?.state ?? history.state;
    this.opdrachtgeverName.set((state?.['opdrachtgeverName'] as string) ?? '');
    this.opdrachtgeverId = (state?.['opdrachtgeverId'] as string) ?? null;
    this.instellingId = (state?.['instellingId'] as string) ?? null;

    this.createbireportService
      .getBookingGroups()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((groups) => {
        this.bookingGroups.set(groups);
        if (groups.length > 0) {
          this.form.patchValue({ bookingGroupId: groups[0].booking_Group_ID });
        }
      });
  }

  goBack(): void {
    this.router.navigateByUrl('proces/exportprocessing/createbireport', {
      state: { opdrachtgeverId: this.opdrachtgeverId, instellingId: this.instellingId },
    });
  }

  onStartVerwerkingClick(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.value as {
      bookingGroupId: string;
      applyWagePartitioning: boolean;
      applyRedistribution: boolean;
    };

    const request: CreateJournalPlanningRecordForBIRequest = {
      importRunId: this.importRunId(),
      bookingGroupId: value.bookingGroupId,
      applyWagePartitioning: value.applyWagePartitioning,
      applyRedistribution: value.applyRedistribution,
    };

    this.isSaving.set(true);
    this.createbireportService
      .createJournalPlanningRecordForBI(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSaving.set(false);
          this.showSuccessDialog();
        },
        error: () => {
          this.isSaving.set(false);
          this.showErrorDialog();
        },
      });
  }

  private showSuccessDialog(): void {
    this.dialogService.open({
      width: '400px',
      title: 'Verwerking gestart',
      message: 'Berekening is gescheduled, zodra dit klaar is wordt de data naar BI gestuurd.',
      confirmText: 'OK',
      variant: 'success',
    });

    this.dialogService
      .confirmed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.goBack());
  }

  private showErrorDialog(): void {
    this.dialogService.open({
      width: '400px',
      title: 'Fout',
      message: 'Er is iets misgegaan bij het starten van de verwerking.',
      confirmText: 'OK',
      variant: 'error',
    });

    this.dialogService.confirmed().pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe();
  }
}
